import {
  SubmissionStatus,
  CampaignStatus,
  UserRole,
  CreatorShortlistStatus,
  CreatorDecisionType,
  CreatorRejectionReason,
  ApprovalTier,
  CampaignUpgrade,
} from './enums.js';
import type { ConceptQueueTab, ContentConceptStatus } from './types.js';

/** Empty status list means all statuses visible to the authenticated role. */
export const CONCEPT_QUEUE_TAB_STATUSES = {
  needs_review: ['PENDING'],
  with_client: ['WITH_CLIENT'],
  awaiting_relay: ['CLIENT_FEEDBACK'],
  sent_back: ['CHANGES'],
  approved: ['APPROVED'],
  all: [],
} as const satisfies Record<ConceptQueueTab, readonly ContentConceptStatus[]>;

// --- Client-visible status set (counting contract) ---

/**
 * Submission statuses the CLIENT role can see — everything that reached (or
 * passed through) the client review gate. Single source of truth for:
 *   - campaign-card Submitted/Approved/Rejected counts (campaigns/service.ts)
 *   - the client submissions list scope + head-collapse (submissions/service.ts)
 *   - the client "Submitted" bucket filter (CampaignReviewsPage.tsx)
 * Both sides MUST import this constant — never hand-copy the list (Rule 11).
 */
export const CLIENT_VISIBLE_SUBMISSION_STATUSES = [
  'WITH_CLIENT',
  'APPROVED',
  'CLIENT_FEEDBACK',
  'CLIENT_APPROVED_WITH_CHANGES',
  'REJECTED',
  // Literal strings (not enum members) so the array stays assignable to the
  // Prisma-generated status union on the API side; `satisfies` still pins every
  // entry to a real SubmissionStatus value.
] as const satisfies readonly `${SubmissionStatus}`[];

/**
 * Client-visible statuses on MANAGED tiers (NO_APPROVAL / LEGAL_ONLY), where the
 * client bought "no review": they see their deliverable (approved content) and never
 * internal compliance rejections (roadmap decision E6). Derived sibling of
 * CLIENT_VISIBLE_SUBMISSION_STATUSES — same single-definition rule (Rule 11); every
 * counting surface picks the list via clientVisibleStatusesForTier(), never inline.
 */
export const MANAGED_TIER_CLIENT_VISIBLE_STATUSES = [
  'APPROVED',
] as const satisfies readonly `${SubmissionStatus}`[];

/** Tiers where Social Soup manages review and the client portal shows approved-only. */
export const MANAGED_APPROVAL_TIERS = [ApprovalTier.NO_APPROVAL, ApprovalTier.LEGAL_ONLY] as const;

/** The one tier selector every counting surface must use (Rule 11). */
export function clientVisibleStatusesForTier(tier: ApprovalTier): readonly `${SubmissionStatus}`[] {
  return (MANAGED_APPROVAL_TIERS as readonly ApprovalTier[]).includes(tier)
    ? MANAGED_TIER_CLIENT_VISIBLE_STATUSES
    : CLIENT_VISIBLE_SUBMISSION_STATUSES;
}

/**
 * NO_APPROVAL spot-check sampling rate (PR-D, roadmap 1.7 — PDF stage-14
 * "30%" rule). Bernoulli probability applied once per post at ingest;
 * both the API draw and any UI copy quote this constant (Rule 11).
 */
export const SPOT_CHECK_SAMPLE_RATE = 0.3;

/** Display labels for the fixed 3-item spot-check checklist (PR-D design D3). */
export const SPOT_CHECK_CHECKLIST_LABELS = {
  disclosureOk: 'Disclosure present (#ad / #gifted)',
  brandSafe: 'Brand-safe',
  onBrief: 'On-brief',
} as const;

export const APPROVAL_TIER_LABELS: Record<ApprovalTier, string> = {
  [ApprovalTier.NO_APPROVAL]: 'No Approval',
  [ApprovalTier.LEGAL_ONLY]: 'Legal Only',
  [ApprovalTier.ONE_ROUND]: '1 Round Feedback',
  [ApprovalTier.FULL]: 'Full Approval',
};

/** Display labels for the sold upgrade flags (PR-B, roadmap 1.2). */
export const CAMPAIGN_UPGRADE_LABELS: Record<CampaignUpgrade, string> = {
  [CampaignUpgrade.HIGH_LEVEL_RECRUITMENT]: 'High-level recruitment',
  [CampaignUpgrade.ADDITIONAL_MODERATION]: 'Additional moderation',
  [CampaignUpgrade.THIRD_PARTY_REVIEWS]: 'Reviews to a 3rd-party site',
  [CampaignUpgrade.VIDEO_TESTIMONIALS]: 'Video testimonials',
  [CampaignUpgrade.LIVE_FEED]: 'Live feed',
  [CampaignUpgrade.WEBINAR_EVENT]: 'Webinar / event',
  [CampaignUpgrade.HYPE_CARD]: 'Hype card',
};

/** Short operator-facing description shown beside the tier badge. */
export const APPROVAL_TIER_DESCRIPTIONS: Record<ApprovalTier, string> = {
  [ApprovalTier.NO_APPROVAL]:
    'Creators post directly; Social Soup spot-checks. No submission pipeline.',
  [ApprovalTier.LEGAL_ONLY]:
    'Social Soup compliance review only — content is not sent to the client.',
  [ApprovalTier.ONE_ROUND]:
    'Client gets exactly one feedback round per content piece; more requires a scope variation.',
  [ApprovalTier.FULL]: 'Unrestricted review rounds (default).',
};

// --- Status Display Labels ---

export const SUBMISSION_STATUS_LABELS: Record<SubmissionStatus, string> = {
  [SubmissionStatus.DRAFT]: 'Draft',
  [SubmissionStatus.PENDING_REVIEW]: 'Pending Review',
  [SubmissionStatus.PENDING_SS_REVIEW]: 'Pending Social Soup Review',
  [SubmissionStatus.WITH_CLIENT]: 'With Client',
  [SubmissionStatus.APPROVED]: 'Approved',
  [SubmissionStatus.CHANGES_REQUESTED]: 'Changes Requested',
  [SubmissionStatus.CLIENT_FEEDBACK]: 'Client Feedback',
  [SubmissionStatus.CLIENT_APPROVED_WITH_CHANGES]: 'Approved with Changes',
  [SubmissionStatus.REJECTED]: 'Rejected',
  [SubmissionStatus.ABANDONED]: 'Closed after review',
};

export const SUBMISSION_STATUS_COLORS: Record<SubmissionStatus, string> = {
  [SubmissionStatus.DRAFT]: '#6B7280',
  [SubmissionStatus.PENDING_REVIEW]: '#F59E0B',
  [SubmissionStatus.PENDING_SS_REVIEW]: '#3B82F6',
  [SubmissionStatus.WITH_CLIENT]: '#8B5CF6',
  [SubmissionStatus.APPROVED]: '#10B981',
  [SubmissionStatus.CHANGES_REQUESTED]: '#F97316',
  [SubmissionStatus.CLIENT_FEEDBACK]: '#A855F7',
  // Conditional approval reads as an approval (green) in single-colour contexts so
  // it groups with APPROVED in lists/charts; the badge adds an amber dot for "with changes".
  [SubmissionStatus.CLIENT_APPROVED_WITH_CHANGES]: '#10B981',
  [SubmissionStatus.REJECTED]: '#EF4444',
  // Slate (closed/terminal), distinct from DRAFT (#6B7280) and REJECTED (red).
  [SubmissionStatus.ABANDONED]: '#64748B',
};

/**
 * Maps a toStatus to the party responsible for action at that point in the
 * workflow. Terminal states (APPROVED, REJECTED, ABANDONED) are omitted —
 * undefined means no "for <party>" suffix is shown in the activity log.
 */
export const SUBMISSION_STATUS_RESPONSIBLE_PARTY: Partial<
  Record<SubmissionStatus, 'Creator' | 'Social Soup' | 'Client'>
> = {
  [SubmissionStatus.DRAFT]: 'Creator',
  [SubmissionStatus.PENDING_REVIEW]: 'Social Soup',
  [SubmissionStatus.PENDING_SS_REVIEW]: 'Social Soup',
  [SubmissionStatus.WITH_CLIENT]: 'Client',
  [SubmissionStatus.CLIENT_FEEDBACK]: 'Social Soup',
  [SubmissionStatus.CLIENT_APPROVED_WITH_CHANGES]: 'Social Soup',
  [SubmissionStatus.CHANGES_REQUESTED]: 'Creator',
  // APPROVED, REJECTED, ABANDONED → omitted (terminal — no action party)
};

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  [CampaignStatus.DRAFT]: 'Draft',
  [CampaignStatus.ACTIVE]: 'Active',
  [CampaignStatus.PAUSED]: 'Paused',
  [CampaignStatus.COMPLETED]: 'Completed',
};

export const CAMPAIGN_STATUS_COLORS: Record<CampaignStatus, string> = {
  [CampaignStatus.DRAFT]: '#6B7280',
  [CampaignStatus.ACTIVE]: '#10B981',
  [CampaignStatus.PAUSED]: '#F59E0B',
  [CampaignStatus.COMPLETED]: '#3B82F6',
};

// --- Campaign Status Transitions ---
// Must mirror backend VALID_CAMPAIGN_TRANSITIONS in campaigns/service.ts

export const VALID_CAMPAIGN_TRANSITIONS: Record<CampaignStatus, CampaignStatus[]> = {
  [CampaignStatus.DRAFT]: [CampaignStatus.ACTIVE],
  [CampaignStatus.ACTIVE]: [CampaignStatus.PAUSED, CampaignStatus.COMPLETED],
  [CampaignStatus.PAUSED]: [CampaignStatus.ACTIVE, CampaignStatus.COMPLETED],
  [CampaignStatus.COMPLETED]: [],
};

// --- Portal Accent Colors ---

export const PORTAL_COLORS: Record<UserRole, string> = {
  [UserRole.ADMIN]: '#FFD60A',
  [UserRole.CLIENT]: '#4DABF7',
  [UserRole.INFLUENCER]: '#FF6B9D',
  [UserRole.AGENT]: '#A78BFA',
};

export const AI_ACCENT_COLOR = '#8B5CF6';

// --- File Upload Limits ---

export const FILE_UPLOAD_LIMITS = {
  video: {
    maxSizeBytes: 500 * 1024 * 1024, // 500MB
    allowedTypes: ['video/mp4', 'video/quicktime', 'video/webm'],
    allowedExtensions: ['.mp4', '.mov', '.webm'],
  },
  image: {
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
    allowedExtensions: ['.jpg', '.jpeg', '.png', '.webp'],
  },
} as const;

// --- Pagination Defaults ---

export const PAGINATION_DEFAULTS = {
  page: 1,
  limit: 20,
  maxLimit: 100,
} as const;

// --- Creator Approval Labels & Colors ---

export const CREATOR_SHORTLIST_STATUS_LABELS: Record<CreatorShortlistStatus, string> = {
  [CreatorShortlistStatus.PENDING]: 'Pending',
  [CreatorShortlistStatus.APPROVED]: 'Approved',
  // "Backup" is the badge label; "held in reserve" is tooltip/helper text only.
  [CreatorShortlistStatus.BACKUP]: 'Backup',
  [CreatorShortlistStatus.REJECTED]: 'Rejected',
  [CreatorShortlistStatus.CHANGES_REQUESTED]: 'Changes Requested',
};

export const CREATOR_SHORTLIST_STATUS_COLORS: Record<CreatorShortlistStatus, string> = {
  [CreatorShortlistStatus.PENDING]: '#F59E0B',
  [CreatorShortlistStatus.APPROVED]: '#10B981',
  // Slate-500 (#64748B) — distinct from ABANDONED token (#64748B shares the value but
  // BACKUP gets its own icon differentiator in the badge to remain distinguishable in
  // grayscale (WCAG 1.4.1). The CSS token used in the badge is --status-backup-* which
  // maps to a cooler mid-slate, NOT the abandoned token.
  [CreatorShortlistStatus.BACKUP]: '#475569',
  [CreatorShortlistStatus.REJECTED]: '#EF4444',
  [CreatorShortlistStatus.CHANGES_REQUESTED]: '#F97316',
};

export const CREATOR_DECISION_LABELS: Record<CreatorDecisionType, string> = {
  [CreatorDecisionType.APPROVED]: 'Approved',
  [CreatorDecisionType.REJECTED]: 'Rejected',
};

export const CREATOR_DECISION_COLORS: Record<CreatorDecisionType, string> = {
  [CreatorDecisionType.APPROVED]: '#2670FF', // Social Soup blue
  [CreatorDecisionType.REJECTED]: '#FF73BA', // Social Soup pink
};

export const CREATOR_REJECTION_REASON_LABELS: Record<CreatorRejectionReason, string> = {
  [CreatorRejectionReason.BRAND_FIT]: 'Brand Fit',
  [CreatorRejectionReason.AUDIENCE_MISMATCH]: 'Audience Mismatch',
  [CreatorRejectionReason.CONTENT_STYLE]: 'Content Style',
  [CreatorRejectionReason.PERFORMANCE_RISK]: 'Performance Risk',
  [CreatorRejectionReason.COMPLIANCE_RISK]: 'Compliance Risk',
  [CreatorRejectionReason.OTHER]: 'Other',
};

// --- Magic Link Defaults ---

export const MAGIC_LINK_DEFAULTS = {
  expiryDays: 30,
  maxExpiryDays: 90,
  minExpiryDays: 1,
} as const;

// --- Media Upload: allowed types + size limits (shared by API validation and web preflight) ---

export const ALLOWED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-msvideo',
  'video/x-matroska',
] as const;

export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
] as const;

export const ALLOWED_MEDIA_TYPES = [...ALLOWED_VIDEO_TYPES, ...ALLOWED_IMAGE_TYPES] as const;

export const MAX_VIDEO_SIZE_BYTES = 500 * 1024 * 1024; // 500 MB
export const MAX_IMAGE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

export function isVideoType(contentType: string): boolean {
  return (ALLOWED_VIDEO_TYPES as readonly string[]).includes(contentType);
}

export function isImageType(contentType: string): boolean {
  return (ALLOWED_IMAGE_TYPES as readonly string[]).includes(contentType);
}

/** Max upload size in bytes for the given MIME type (video 500MB, image 50MB). */
export function getMaxSize(contentType: string): number {
  return isVideoType(contentType) ? MAX_VIDEO_SIZE_BYTES : MAX_IMAGE_SIZE_BYTES;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// ─── Audience demographic facets (granular shortlist visibility) ──────────────

/** The demographic facets a shortlist editor can individually show/hide. */
export const AUDIENCE_FACETS = ['gender', 'age', 'country'] as const;
export type AudienceFacet = (typeof AUDIENCE_FACETS)[number];

/** Human labels for the facets (UI). "country" surfaces to users as "Location". */
export const AUDIENCE_FACET_LABELS: Record<AudienceFacet, string> = {
  gender: 'Gender',
  age: 'Age',
  country: 'Location',
};

/** Default visibility when audienceVisibility is null/unset: every facet visible. */
export const DEFAULT_AUDIENCE_VISIBILITY: Record<AudienceFacet, boolean> = {
  gender: true,
  age: true,
  country: true,
};

/**
 * Normalise a stored (possibly null / partial) audienceVisibility JSON into a full
 * boolean map. Missing facets default to visible (true) so legacy rows are unchanged.
 */
export function resolveAudienceVisibility(
  raw: Partial<Record<AudienceFacet, boolean>> | null | undefined,
): Record<AudienceFacet, boolean> {
  if (!raw) return { ...DEFAULT_AUDIENCE_VISIBILITY };
  return {
    gender: raw.gender !== false,
    age: raw.age !== false,
    country: raw.country !== false,
  };
}

// ─── C2: Audience age display/write normalization ─────────────────────────
// creator-shortlist-refinements-v2 Phase 1, frozen contract C2
// (docs/plans/creator-shortlist-refinements-v2-contracts.md). These are the
// ONLY pure helpers for audience-age bracket handling; do not re-implement
// dash-spelling/legacy-65 logic elsewhere (Rule 11 — single source of truth).

/**
 * Age brackets hidden from every display surface (minors). Currently just the
 * 13-17 bracket. Compared via `normalizedAgeLabel` so any dash spelling of the
 * same bracket is caught, not only the exact ASCII-hyphen string.
 */
export const HIDDEN_AGE_BRACKETS: readonly string[] = ['13-17'];

/** Canonical open-ended "65 and up" bracket, as rendered to every reader. */
const CANONICAL_65_PLUS_LABEL = '65+';
/** Legacy stored spelling of the same open-ended bracket ("65-", no upper bound). */
const LEGACY_65_LABEL = '65-';

/**
 * Dash-family characters accepted as equivalent to a plain ASCII hyphen when
 * comparing age-bracket labels: hyphen variants, en dash, em dash and the
 * Unicode minus sign. Never used to REWRITE a stored/returned label — only to
 * decide whether two spellings denote the same bracket.
 */
const DASH_CHARS_PATTERN = /[‐‑‒–—−]/g;

/** Dash-normalized, trimmed label used ONLY for bracket-identity comparisons. */
function normalizedAgeLabel(label: string): string {
  return label.replace(DASH_CHARS_PATTERN, '-').trim();
}

/** True when `label` denotes the hidden 13-17 bracket, under any dash spelling. */
function isHiddenAgeLabel(label: string): boolean {
  const normalized = normalizedAgeLabel(label);
  return HIDDEN_AGE_BRACKETS.some((hidden) => normalized === hidden);
}

/**
 * Write-time filter (C2): strips incoming 13-17 keys (any dash spelling) from
 * a NEW manual/add/import/copy/enrichment audience-age payload before it is
 * persisted. Every other key/value passes through completely UNCHANGED — no
 * renaming, no percentage re-normalization. Never call this against an
 * EXISTING stored row merely to "clean" a legacy hidden bucket; C2's write-time
 * lock guard and the no-backfill rule both depend on this being applied only
 * to genuinely new payloads at the point they are about to be written.
 */
export function filterHiddenAudienceAgesForWrite(
  data: Record<string, number>,
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const [label, value] of Object.entries(data)) {
    if (isHiddenAgeLabel(label)) continue;
    result[label] = value;
  }
  return result;
}

/**
 * True when an audience facet carries at least one finite value above zero.
 * `null`, `{}`, and an all-zero payload (`{ male: 0, female: 0 }`) are all
 * "nothing measured" — they must never be treated as creator-provided data.
 * Accepts `unknown` so a stored `Json` column can be passed without a cast;
 * anything that is not a plain object is "nothing measured".
 */
export function hasSubstantiveAudienceFacet(data: unknown): boolean {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return false;
  return Object.values(data as Record<string, unknown>).some(
    (value) => typeof value === 'number' && Number.isFinite(value) && value > 0,
  );
}

/**
 * True when ANY of the three demographic facets is substantive. This is the
 * shared definition of "this row actually holds audience data" — used by the
 * write path to decide whether a manual edit may take ownership of the row
 * (`audienceDataLocked`) and by the thumbnail card to decide whether there is
 * anything to draw. A blank or all-zero import must NOT lock the row: a lock
 * over nothing silently blocks every future provider refresh forever.
 */
export function hasSubstantiveAudienceData(facets: {
  gender?: unknown;
  age?: unknown;
  country?: unknown;
}): boolean {
  return (
    hasSubstantiveAudienceFacet(facets.gender) ||
    hasSubstantiveAudienceFacet(facets.age) ||
    hasSubstantiveAudienceFacet(facets.country)
  );
}

/**
 * `CreatorShortlist.audienceDataSource` value for a snapshot that was NOT
 * measured for this creator but derived from comparable creators' measured
 * audiences (used only when the provider has no demographics for the
 * account). Every surface that draws audience data must disclose it.
 */
export const ESTIMATED_AUDIENCE_SOURCE = 'ESTIMATED';

export function isEstimatedAudienceSource(source: string | null | undefined): boolean {
  return source === ESTIMATED_AUDIENCE_SOURCE;
}

/**
 * Read-time display normalization (C2), shared by every other helper below:
 * - hides the 13-17 bracket (any dash spelling), read-time only — never
 *   mutates/backfills the stored row;
 * - drops non-finite/negative values, retains an honest zero;
 * - maps a legacy "65-" bracket to the canonical "65+" label; if BOTH a
 *   legacy and canonical 65 entry exist, the canonical one wins and the
 *   legacy one is dropped — the two are never summed;
 * - every other label (including anything unrecognized) is retained
 *   verbatim, in its original insertion order — this function does not sort.
 */
export function normalizeAudienceAgeEntries(data: Record<string, number>): Array<[string, number]> {
  const entries = Object.entries(data);
  const hasCanonical65 = entries.some(
    ([label]) => normalizedAgeLabel(label) === CANONICAL_65_PLUS_LABEL,
  );

  const result: Array<[string, number]> = [];
  let legacy65Promoted = false;
  for (const [label, value] of entries) {
    if (isHiddenAgeLabel(label)) continue;
    if (!Number.isFinite(value) || value < 0) continue;

    if (normalizedAgeLabel(label) === LEGACY_65_LABEL) {
      if (hasCanonical65 || legacy65Promoted) continue; // never sum duplicated representations
      legacy65Promoted = true;
      result.push([CANONICAL_65_PLUS_LABEL, value]);
      continue;
    }
    result.push([label, value]);
  }
  return result;
}

/** The leading numeric age in a bracket label (e.g. "25-34" -> 25), or null when none. */
function leadingAgeNumber(label: string): number | null {
  const match = label.match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Chronological order (C2): filtered/normalized entries sorted by leading
 * numeric age ascending, with non-numeric labels sorted last. Equal leading
 * numbers break ties by label so repeated calls are deterministic. Used by
 * full profile displays (the only surface that sorts ages chronologically).
 */
export function sortAudienceAgeEntries(data: Record<string, number>): Array<[string, number]> {
  return normalizeAudienceAgeEntries(data).sort(([labelA], [labelB]) => {
    const numA = leadingAgeNumber(labelA);
    const numB = leadingAgeNumber(labelB);
    if (numA === null && numB === null) return 0;
    if (numA === null) return 1;
    if (numB === null) return -1;
    if (numA !== numB) return numA - numB;
    return labelA.localeCompare(labelB);
  });
}

/**
 * Top-N entries by percentage (C2): filtered/normalized entries sorted by
 * value descending, tie-broken chronologically (leading numeric age
 * ascending) for determinism, then sliced to `limit` (default 3). Filtering
 * happens BEFORE ranking/slicing, so a large 13-17 bucket can never crowd out
 * an eligible bracket from the top three.
 */
export function topAudienceAgeEntries(
  data: Record<string, number>,
  limit = 3,
): Array<[string, number]> {
  const chronological = sortAudienceAgeEntries(data);
  const chronoIndex = new Map(chronological.map(([label], index) => [label, index]));
  return normalizeAudienceAgeEntries(data)
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return (chronoIndex.get(a[0]) ?? 0) - (chronoIndex.get(b[0]) ?? 0);
    })
    .slice(0, limit);
}
